import { collection, getDocs, limit, query, where } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { db } from '../../App';
import ShareButton from '../../components/ShareButton';
import { AlbumElementType, PhotoElementType, VideoElementType } from '../../global/types';

type MediaDetailsProps = {
    type: 'photo' | 'video';
};

type MediaState = {
    album?: AlbumElementType;
    item: PhotoElementType | VideoElementType;
};

const MediaDetails: React.FC<MediaDetailsProps> = ({ type }) => {
    const { albumSlug, photoSlug, videoSlug } = useParams();
    const [media, setMedia] = useState<MediaState | null>(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        let active = true;

        const findBySlug = async (path: string, slug: string) => {
            const snapshot = await getDocs(query(collection(db, path), where('slug', '==', slug), limit(1)));
            return snapshot.docs[0];
        };

        const loadMedia = async () => {
            setLoading(true);
            setNotFound(false);

            try {
                if (type === 'photo' && albumSlug && photoSlug) {
                    const albumDocument = await findBySlug('mediatheque', albumSlug);
                    if (!albumDocument || albumDocument.data().category !== 'photos') throw new Error('not-found');

                    const photoDocument = await findBySlug(`mediatheque/${albumDocument.id}/photos`, photoSlug);
                    if (!photoDocument) throw new Error('not-found');

                    if (active) {
                        setMedia({
                            album: { ...albumDocument.data(), id: albumDocument.id } as unknown as AlbumElementType,
                            item: { ...photoDocument.data(), id: photoDocument.id } as unknown as PhotoElementType,
                        });
                    }
                } else if (type === 'video' && videoSlug) {
                    const videoDocument = await findBySlug('mediatheque', videoSlug);
                    if (!videoDocument || videoDocument.data().category !== 'videos') throw new Error('not-found');

                    if (active) {
                        setMedia({
                            item: { ...videoDocument.data(), id: videoDocument.id } as unknown as VideoElementType,
                        });
                    }
                } else {
                    throw new Error('not-found');
                }
            } catch {
                if (active) {
                    setMedia(null);
                    setNotFound(true);
                }
            } finally {
                if (active) setLoading(false);
            }
        };

        loadMedia();
        return () => { active = false; };
    }, [albumSlug, photoSlug, type, videoSlug]);

    useEffect(() => {
        if (!media?.item.title) return;
        const previousTitle = document.title;
        document.title = `${media.item.title} | FLAG`;
        return () => { document.title = previousTitle; };
    }, [media]);

    if (loading) {
        return <div className="mx-auto my-[10vh] rounded-xl bg-white p-8 text-gray-500">Chargement...</div>;
    }

    if (notFound || !media) {
        return (
            <div className="mx-auto my-[10vh] max-w-xl rounded-xl bg-white p-8 text-center">
                <h1 className="mb-3 text-2xl font-medium">Média introuvable</h1>
                <p className="mb-5 text-gray-600">Ce lien est incorrect ou le média n'est plus disponible.</p>
                <Link className="text-primary underline" to={type === 'photo' ? '/mediatheque/photos' : '/mediatheque/videos'}>
                    Retour à la médiathèque
                </Link>
            </div>
        );
    }

    const item = media.item;
    const photo = type === 'photo' ? item as PhotoElementType : null;
    const video = type === 'video' ? item as VideoElementType : null;

    return (
        <article className="mx-2 my-6 max-w-5xl rounded-xl bg-white p-4 sm:p-6 lg:mx-auto lg:my-[10vh] lg:p-8">
            {photo?.imageUrl ? (
                <img className="max-h-[72vh] w-full rounded-lg object-contain" src={photo.imageUrl} alt={photo.title} />
            ) : null}
            {video?.videoUrl ? (
                <video className="max-h-[72vh] w-full rounded-lg bg-black" src={video.videoUrl} poster={video.posterUrl} controls />
            ) : null}

            <div className="mt-5 flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
                <div>
                    <h1 className="text-2xl font-medium text-black lg:text-3xl">{item.title}</h1>
                    {item.subtitle ? <p className="mt-2 text-gray-600">{item.subtitle}</p> : null}
                    {media.album?.slug ? (
                        <Link className="mt-3 inline-block text-primary underline" to={`/mediatheque/photos/${media.album.slug}`}>
                            Voir l'album {media.album.title}
                        </Link>
                    ) : null}
                </div>
                <ShareButton title={item.title} />
            </div>
        </article>
    );
};

export default MediaDetails;
